import React from 'react';
import { Button } from '@roger/ui-components';
import { Loader2, CheckCircle } from 'lucide-react';
import type {
  KinnserCreatePatientResponse,
  RogerOnboardPatientResponse,
} from '../../services/api/types.gen';
import type { KinnserOnboardPatientResponse } from '../../hooks/useCreatePatientForm/useKinnserOnboarding';

interface PatientCreateButtonProps {
  handleCreate: () => Promise<void>;
  onComplete?: () => Promise<void>;
  creating: boolean;
  saveStatus: string;
  loadingClinics: boolean;
  loadingClinicData: boolean;
  selectedIntake: { status?: string } | null;
  isUsingOnboarding?: boolean;
  /** When false, button never shows success state (e.g. after "Mark Incomplete") */
  pushToEmrTabComplete?: boolean;
  createResult:
    | KinnserCreatePatientResponse
    | KinnserOnboardPatientResponse
    | RogerOnboardPatientResponse
    | null;
  isValid: boolean;
  pushToRoger?: boolean | null;
  pushToKinnser?: boolean | null;
  className?: string;
}

export const PatientCreateButton: React.FC<PatientCreateButtonProps> = ({
  handleCreate,
  onComplete,
  creating,
  saveStatus,
  loadingClinics,
  loadingClinicData,
  selectedIntake,
  isUsingOnboarding,
  pushToEmrTabComplete = true,
  createResult,
  isValid,
  pushToRoger,
  pushToKinnser,
  className,
}) => {
  const emrName = pushToRoger ? 'Roger' : pushToKinnser ? 'Kinnser' : 'NONE';
  const isRogerSuccess =
    pushToRoger &&
    (selectedIntake?.status === 'pushed_to_roger' ||
      (createResult as RogerOnboardPatientResponse)?.success === true);
  const isKinnserSuccess =
    pushToKinnser &&
    (selectedIntake?.status === 'pushed_to_kinnser' ||
      (isUsingOnboarding
        ? (createResult as KinnserOnboardPatientResponse | null)
            ?.patient_creation.success === true
        : (createResult as KinnserCreatePatientResponse | null)?.success ===
          true));

  const isSuccess =
    pushToEmrTabComplete && (isRogerSuccess || isKinnserSuccess);

  // Button should be disabled if neither EMR is selected
  const noEmrSelected = !pushToRoger && !pushToKinnser;

  return (
    <Button
      onClick={async () => {
        await handleCreate();
        // Refresh intake data after successful creation
        if (onComplete) {
          await onComplete();
        }
      }}
      disabled={
        noEmrSelected ||
        creating ||
        saveStatus === 'saving' ||
        loadingClinics ||
        loadingClinicData ||
        isSuccess ||
        !isValid
      }
      className={className}
    >
      {creating ? (
        <>
          <Loader2 className='h-4 w-4 animate-spin mr-2' />
          {isUsingOnboarding ? 'Onboarding...' : 'Creating...'}
        </>
      ) : isSuccess ? (
        <>
          <CheckCircle className='h-4 w-4 mr-2' />
          {isUsingOnboarding
            ? `Patient Onboarded in ${emrName}`
            : `Patient Created in ${emrName}`}
        </>
      ) : (
        <>
          <CheckCircle className='h-4 w-4 mr-2' />
          {isUsingOnboarding ? `Onboard in ${emrName}` : `Create in ${emrName}`}
        </>
      )}
    </Button>
  );
};
